// Igénylés-ELUTASÍTÁS megerősítő dialógusa — a közös ConfirmDialog nem fogad extra tartalmat (children),
// ezért ez egy kis dedikált dialógus a közös Dialog-primitíveken (a ConfirmDialog vizuális mintáját követve:
// danger-ikon + destruktív megerősítő gomb), KIEGÉSZÍTVE az opcionális indok-mezővel (max 1000 karakter —
// a contract nullableStr(1000) korlátja). A közös primitívhez nem nyúlunk, a ConfirmDialog-ot nem forkoljuk.
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface AssetRejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Az elutasítandó igénylés neve (a megerősítő szövegbe). */
  assetName: string;
  /** Folyamatban lévő mentés — a gombok tiltva, a bezárás blokkolva. */
  pending?: boolean;
  /** Megerősítés — a (trimmelt) indokkal; üres indok → null-ként megy a wire-re (a hívó normalizál). */
  onConfirm: (reason: string) => void;
}

export function AssetRejectDialog({
  open,
  onOpenChange,
  assetName,
  pending = false,
  onConfirm,
}: AssetRejectDialogProps): JSX.Element {
  const { t } = useTranslation();
  const reasonId = useId();
  const [reason, setReason] = useState('');

  // Nyitáskor tiszta indok-mező (az előző elutasítás szövege ne ragadjon be).
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('assets.workflow.reject.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {/* Súlyosság-jel a tartalomban (a ConfirmDialog danger-mintája). */}
          <div className="flex gap-2.5">
            <ExclamationTriangleIcon
              className="text-danger-600 mt-0.5 size-5 shrink-0"
              aria-hidden="true"
            />
            <DialogDescription>
              {t('assets.workflow.reject.desc', { name: assetName })}
            </DialogDescription>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={reasonId}>{t('assets.workflow.reject.reasonLabel')}</Label>
            <Textarea
              id={reasonId}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('assets.workflow.reject.reasonPlaceholder')}
              rows={3}
              maxLength={1000}
              disabled={pending}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t('assets.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => onConfirm(reason.trim())}
            disabled={pending}
          >
            {pending ? t('assets.workflow.working') : t('assets.workflow.reject.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
